import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    paymentMethod = "paymentMethod",
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
    showCostOfEventPerPerson = "showCostOfEventPerPerson",
    showCostOfEvent = "showCostOfEvent",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.paymentMethod]: string;
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
        [fields.showCostOfEventPerPerson]: string;
        [fields.showCostOfEvent]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    paymentMethod: state.components.contactUsForm.fieldValues["paymentMethod"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PaymentMethodStyles {
    paymentMethodTextField?: TextFieldPresentationProps;
    label?: InjectableCss;
    costOfEventWrapper?: InjectableCss;
    costOfEventPerPersonWrapper?: InjectableCss;
}

export const paymentMethodStyles: PaymentMethodStyles = {
    label: {
        css: css`
            min-height: 22px;
            display: inline-block;
            width: 100%;
            font-size: 15px;
            font-weight: 600;
        `,
    },
    costOfEventWrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    costOfEventPerPersonWrapper: {
        css: css`
            padding-top: 20px;
        `,
    },
};

const styles = paymentMethodStyles;

const PaymentMethod: React.FC<Props> = ({ fields, paymentMethod, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [paymentMethodErrorMessage, setPaymentMethodErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.paymentMethod = validatePaymentMethod;
        if (typeof paymentMethod !== "undefined") {
            validatePaymentMethod();
        }
    }, [paymentMethod]);

    React.useEffect(() => {
        contactFormContext.validators.paymentMethod = validatePaymentMethod;
    }, [isRequired]);

    const validatePaymentMethod = () => {
        const errorMessage = isRequired && !paymentMethod ? (siteMessage("Field_Required", label) as string) : "";
        setPaymentMethodErrorMessage(errorMessage);
        return !errorMessage;
    };

    const paymentMethodChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "paymentMethod", value: event.target.value });
    };

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.costOfEventWrapper}>
                <Typography {...styles.label}> Cost Of Event </Typography>
                <StyledWrapper>{fields.showCostOfEvent}</StyledWrapper>
            </StyledWrapper>
            <StyledWrapper>
                <TextField
                    {...styles.paymentMethodTextField}
                    label={label}
                    required={isRequired}
                    placeholder={hintText}
                    value={paymentMethod || ""}
                    error={paymentMethodErrorMessage}
                    onChange={paymentMethodChangeHandler}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.costOfEventPerPersonWrapper}>
                <Typography {...styles.label}> Cost Of Event Per Person </Typography>
                <StyledWrapper>{parse(fields.showCostOfEventPerPerson)}</StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PaymentMethod),
    definition: {
        group: "Contact Us",
        fieldDefinitions: [
            {
                name: fields.paymentMethod,
                displayName: "Payment Method",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label for Payment Method",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Payment Method",
                isRequired: true,
            },
            {
                name: fields.showCostOfEvent,
                displayName: "Show Cost Of Event",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "multi line placeholder",
                isRequired: true,
            },
            {
                fieldType: "Translatable",
                name: fields.showCostOfEventPerPerson,
                editorTemplate: "RichTextField",
                displayName: "Show cost of event per person.",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
        ],
    },
};

export default widgetModule;
